import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Play, Star, ChevronRight, ChevronLeft, Heart, Clock, Film, Tv, Clapperboard, RotateCcw } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import api from "@/services/api";
import { proxyImageUrl } from "@/utils/imageProxy";
import { getFavorites, getContinueWatching, getWatchHistory, getChannelStats } from "@/utils/watchData";
import { MovieDetailModal } from "@/pages/app/MoviesPage";

function ScrollRow({ title, icon: Icon, children, viewAllPath }) {
  const scrollRef = useRef(null);
  const [canL, setCanL] = useState(false);
  const [canR, setCanR] = useState(false);
  const navigate = useNavigate();

  const check = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanL(el.scrollLeft > 10);
    setCanR(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  }, []);

  useEffect(() => {
    check();
    const el = scrollRef.current;
    if (el) el.addEventListener("scroll", check);
    return () => el?.removeEventListener("scroll", check);
  }, [check, children]);

  const scroll = (dir) => scrollRef.current?.scrollBy({ left: dir * 400, behavior: "smooth" });

  return (
    <div className="relative group/row">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-2xl font-semibold flex items-center gap-3 text-white">
          {Icon && <Icon className="w-5 h-5 text-blue-500" />}
          {title}
        </h2>
        {viewAllPath && (
          <button
            onClick={() => navigate(viewAllPath)}
            className="flex items-center gap-1 text-sm text-zinc-500 hover:text-white transition-colors"
          >
            Hamısı <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="relative">
        {canL && (
          <button onClick={() => scroll(-1)} className="absolute left-0 top-0 bottom-0 z-10 w-10 flex items-center justify-center bg-gradient-to-r from-[#09090b] to-transparent opacity-0 group-hover/row:opacity-100 transition-opacity">
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}
        <div ref={scrollRef} className="flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: "none" }}>
          {children}
        </div>
        {canR && (
          <button onClick={() => scroll(1)} className="absolute right-0 top-0 bottom-0 z-10 w-10 flex items-center justify-center bg-gradient-to-l from-[#09090b] to-transparent opacity-0 group-hover/row:opacity-100 transition-opacity">
            <ChevronRight className="w-6 h-6" />
          </button>
        )}
      </div>
    </div>
  );
}

function LandscapeCard({ id, name, poster, type, onClick, progress, currentTime, duration, year, compact }) {
  // Calculate remaining time (e.g. "2 saat 14 dəqiqə qaldı" or "45 dəqiqə qaldı")
  let remainingLabel = null;
  if (duration && currentTime) {
    const secs = Math.max(0, duration - currentTime);
    const hours = Math.floor(secs / 3600);
    const mins = Math.round((secs % 3600) / 60);
    if (hours > 0) remainingLabel = `${hours} saat ${mins} dəqiqə qaldı`;
    else remainingLabel = `${mins} dəqiqə qaldı`;
  }
  const widthClass = compact ? "w-[227px]" : "w-[252px]";
  return (
    <div onClick={() => onClick(id, type)} className={`flex-shrink-0 ${widthClass} group cursor-pointer`} data-testid={`landscape-card-${type}-${id}`}>
      <div className="relative aspect-video rounded-2xl overflow-hidden bg-zinc-800/50">
        {poster ? (
          <img src={proxyImageUrl(poster)} alt={name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><Film className="w-8 h-8 text-zinc-600" /></div>
        )}
        {/* Bottom gradient + text overlay */}
        <div className="absolute inset-x-0 bottom-0 pt-16 pb-4 px-3 bg-gradient-to-t from-black/95 via-black/70 to-transparent">
          <p className="text-sm font-semibold truncate text-white">{name}</p>
          {remainingLabel ? (
            <p className="text-xs text-zinc-300 mt-0.5">{remainingLabel}</p>
          ) : year ? (
            <p className="text-xs text-zinc-400 mt-0.5">{year}</p>
          ) : null}
        </div>
        {/* Hover play button */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
          </div>
        </div>
        {/* Progress bar — bottom edge of card */}
        {progress > 0 && (
          <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-zinc-700">
            <div className="h-full bg-red-500" style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>
    </div>
  );
}

function PosterCard({ id, name, poster, year, rating, type, onClick, badge, progress }) {
  return (
    <div onClick={() => onClick(id, type)} className="flex-shrink-0 w-[176px] group cursor-pointer" data-testid={`card-${type}-${id}`}>
      <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-zinc-800/50 mb-1.5">
        {poster ? (
          <img src={proxyImageUrl(poster)} alt={name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><Film className="w-8 h-8 text-zinc-600" /></div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Play className="w-4 h-4 text-white ml-0.5" fill="white" />
          </div>
        </div>
        {badge && (
          <div className="absolute top-2 left-2 px-2 py-0.5 bg-red-600 text-[10px] font-semibold rounded">{badge}</div>
        )}
        {rating > 0 && (
          <div className="absolute top-2 right-2 flex items-center gap-0.5 px-1.5 py-0.5 bg-black/60 backdrop-blur-sm rounded text-[10px] font-medium text-yellow-400">
            <Star className="w-2.5 h-2.5" fill="currentColor" />{rating}
          </div>
        )}
        {progress > 0 && (
          <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-zinc-700">
            <div className="h-full bg-red-500" style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>
      <p className="text-xs font-medium truncate text-zinc-300 group-hover:text-white transition-colors">{name}</p>
      {year && <p className="text-[10px] text-zinc-600">{year}</p>}
    </div>
  );
}

function ChannelCard({ channel, onClick }) {
  return (
    <div onClick={() => onClick(channel)} className="flex-shrink-0 w-[187px] group cursor-pointer" data-testid={`channel-card-${channel.stream_id}`}>
      <div className="relative aspect-[3/2] rounded-xl overflow-hidden bg-zinc-800/50 mb-2 p-2 flex items-center justify-center">
        {channel.stream_icon ? (
          <img src={proxyImageUrl(channel.stream_icon)} alt={channel.name} className="max-w-[90%] max-h-[90%] object-contain transition-transform duration-300 group-hover:scale-110" loading="lazy" onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          <Tv className="w-12 h-12 text-zinc-600" />
        )}
        <div className="absolute top-2 left-2 px-2 py-0.5 bg-red-600 text-[10px] font-semibold rounded">CANLI</div>
      </div>
      <p className="text-sm font-medium truncate text-zinc-400 group-hover:text-white transition-colors text-center">{channel.name}</p>
    </div>
  );
}

export default function HomePage() {
  const [liveChannels, setLiveChannels] = useState([]);
  const [movies, setMovies] = useState([]);
  const [series, setSeries] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [continueWatching, setContinueWatching] = useState([]);
  const [watchHistory, setWatchHistory] = useState([]);
  const [channelStats, setChannelStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [heroes, setHeroes] = useState([]);
  const [heroIdx, setHeroIdx] = useState(0);
  const [heroDetailsMap, setHeroDetailsMap] = useState({});
  const [selectedMovie, setSelectedMovie] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const heroMovie = heroes[heroIdx] || null;
  const heroDetails = heroMovie ? heroDetailsMap[heroMovie.stream_id] : null;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [liveData, vodData, seriesData] = await Promise.all([
          api.getLiveContent({ per_page: 20 }),
          api.getVodContent({ per_page: 20, order_by: 'addtime_desc' }),
          api.getSeriesContent({ per_page: 20, order_by: 'addtime_desc' }),
        ]);
        setLiveChannels(liveData.content || []);
        setMovies(vodData.content || []);
        setSeries(seriesData.content || []);

        // Server banners come FIRST in rotation, followed by random movie heroes
        let serverBanners = [];
        try {
          const resp = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/banners?location=home`);
          serverBanners = resp.data.banners || [];
        } catch { /* fallback to movie heroes */ }

        // Hero rotation: pick 5 random unique movies
        const movs = vodData.content || [];
        let pickedHeroes = [];
        if (movs.length > 0) {
          const shuffled = [...movs].sort(() => Math.random() - 0.5);
          pickedHeroes = shuffled.slice(0, Math.min(5, shuffled.length));
          // Preload details for all heroes in parallel
          pickedHeroes.forEach((h) => {
            api.getVodDetails(h.stream_id)
              .then((d) => setHeroDetailsMap((prev) => ({ ...prev, [h.stream_id]: d.info || {} })))
              .catch(() => {});
          });
        }
        setHeroes([...serverBanners.map((b) => ({ ...b, isBanner: true })), ...pickedHeroes]);
      } catch (err) {
        console.error("Failed to fetch:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Load localStorage data and listen for changes
  useEffect(() => {
    const refresh = () => {
      setFavorites(getFavorites());
      setContinueWatching(getContinueWatching());
      setWatchHistory(getWatchHistory());
      setChannelStats(getChannelStats());
    };
    refresh();
    window.addEventListener("fibernet-favorites-changed", refresh);
    window.addEventListener("fibernet-progress-changed", refresh);
    window.addEventListener("fibernet-history-changed", refresh);
    return () => {
      window.removeEventListener("fibernet-favorites-changed", refresh);
      window.removeEventListener("fibernet-progress-changed", refresh);
      window.removeEventListener("fibernet-history-changed", refresh);
    };
  }, []);

  // Auto-rotate hero every 8s
  useEffect(() => {
    if (heroes.length < 2) return;
    const t = setInterval(() => {
      setHeroIdx((i) => (i + 1) % heroes.length);
    }, 8000);
    return () => clearInterval(t);
  }, [heroes.length]);

  const goToContent = (id, type) => {
    if (type === "vod") {
      const movie = movies.find(m => m.stream_id === id);
      if (movie) setSelectedMovie(movie);
    }
    else if (type === "series") navigate(`/app/series`);
    else if (type === "live") navigate("/app/live", { state: { channelId: id } });
    else navigate(`/app/player/${type}/${id}`);
  };

  const handlePlayMovie = (movie) => {
    setSelectedMovie(null);
    navigate(`/app/player/vod/${movie.stream_id}`, { state: { returnPath: location.pathname } });
  };

  const goToChannel = (channel) => {
    navigate("/app/live", { state: { channelId: channel.stream_id } });
  };

  // Hybrid sort: channels with view stats float to top by score (count*10 + recency bonus)
  const sortedLiveChannels = useMemo(() => {
    const now = Date.now();
    const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
    return [...liveChannels].sort((a, b) => {
      const sa = channelStats[a.stream_id] || { count: 0, lastWatched: 0 };
      const sb = channelStats[b.stream_id] || { count: 0, lastWatched: 0 };
      const freshA = sa.lastWatched && (now - sa.lastWatched < SEVEN_DAYS) ? 5 : 0;
      const freshB = sb.lastWatched && (now - sb.lastWatched < SEVEN_DAYS) ? 5 : 0;
      const scoreA = sa.count * 10 + freshA;
      const scoreB = sb.count * 10 + freshB;
      return scoreB - scoreA;
    });
  }, [liveChannels, channelStats]);

  if (loading) {
    return (
      <div data-testid="home-loading" className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-zinc-500">Yüklənir...</span>
        </div>
      </div>
    );
  }

  const heroBackdrop = heroDetails?.backdrop || heroDetails?.backdrop_path?.[0];
  const isBanner = !!heroMovie?.isBanner;
  const bannerImg = (url) => (url?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);

  const handleBannerPlay = (b) => {
    if (b.link_type === "vod" && b.link_value) navigate(`/app/player/vod/${b.link_value}`, { state: { returnPath: location.pathname } });
    else if (b.link_type === "live" && b.link_value) navigate("/app/live", { state: { channelId: b.link_value } });
    else if (b.link_type === "url" && b.link_value) window.open(b.link_value, "_blank");
  };

  return (
    <div data-testid="home-page" className="space-y-8">
      {/* Hero Banner */}
      {heroMovie && (
        <div className="relative h-[280px] sm:h-[360px] md:h-[420px] overflow-hidden">
          {isBanner ? (
            <img key={heroMovie.id} src={bannerImg(heroMovie.image_url)} alt="" className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500" />
          ) : heroBackdrop ? (
            <img key={heroMovie.stream_id} src={proxyImageUrl(heroBackdrop)} alt="" className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500" />
          ) : heroMovie.stream_icon ? (
            <img key={heroMovie.stream_id} src={proxyImageUrl(heroMovie.stream_icon)} alt="" className="absolute inset-0 w-full h-full object-cover blur-md scale-110 opacity-60" />
          ) : null}
          <div className="absolute inset-0 bg-gradient-to-r from-[#09090b] via-[#09090b]/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-transparent to-[#09090b]/30" />

          <div className="relative z-10 h-full flex flex-col justify-end p-5 sm:p-6 md:p-8 max-w-lg">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 leading-tight">{isBanner ? heroMovie.title : heroMovie.name}</h1>
            {!isBanner && (
            <div className="flex items-center gap-3 text-sm text-zinc-400 mb-2">
              {heroMovie.release_date && <span>{heroMovie.release_date.slice(0, 4)}</span>}
              {heroDetails?.duration && <span>{heroDetails.duration}</span>}
              {heroMovie.rating && (
                <span className="flex items-center gap-1 text-yellow-400">
                  <Star className="w-3.5 h-3.5" fill="currentColor" />
                  {parseFloat(heroMovie.rating).toFixed(1)}
                </span>
              )}
            </div>
            )}
            {isBanner && heroMovie.description && (
              <p className="text-sm text-zinc-400 mb-5 line-clamp-2">{heroMovie.description}</p>
            )}
            {!isBanner && heroDetails?.plot && (
              <p className="text-sm text-zinc-400 mb-5 line-clamp-2">{heroDetails.plot}</p>
            )}
            <div className="flex gap-3">
              {(!isBanner || (heroMovie.link_type && heroMovie.link_type !== "none" && heroMovie.link_value)) && (
              <button
                data-testid="hero-play-btn"
                onClick={() => isBanner ? handleBannerPlay(heroMovie) : navigate(`/app/player/vod/${heroMovie.stream_id}`, { state: { returnPath: location.pathname } })}
                className="flex items-center gap-2 px-6 py-2.5 bg-white text-black font-semibold rounded-lg hover:bg-zinc-200 transition-colors text-sm"
              >
                <Play className="w-4 h-4" fill="currentColor" /> {isBanner && heroMovie.link_type === "live" ? "İzlə" : "Oynat"}
              </button>
              )}
              <button
                onClick={() => navigate("/app/movies")}
                className="flex items-center gap-2 px-6 py-2.5 bg-zinc-700/60 backdrop-blur-sm font-semibold rounded-lg hover:bg-zinc-600/60 transition-colors text-sm"
              >
                Filmlər
              </button>
            </div>
          </div>

          {/* Dot indicators (auto-rotate only, no manual arrows) */}
          {heroes.length > 1 && (
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex gap-2">
              {heroes.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setHeroIdx(i)}
                  data-testid={`hero-dot-${i}`}
                  className={`h-1.5 rounded-full transition-all ${i === heroIdx ? 'w-6 bg-white' : 'w-1.5 bg-white/40 hover:bg-white/70'}`}
                  aria-label={`Go to hero ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="px-4 sm:px-6 space-y-7">
        {/* Live Channels */}
        {sortedLiveChannels.length > 0 && (
          <ScrollRow title="Canlı TV" icon={Tv} viewAllPath="/app/live">
            {sortedLiveChannels.slice(0, 20).map((ch) => (
              <ChannelCard key={ch.stream_id} channel={ch} onClick={goToChannel} />
            ))}
          </ScrollRow>
        )}

        {/* Continue Watching */}
        {continueWatching.length > 0 && (
          <ScrollRow title="Qaldığı yerdən davam et" icon={RotateCcw}>
            {continueWatching.map((item) => (
              <LandscapeCard
                key={item.id}
                id={item.id}
                name={item.name}
                poster={item.poster}
                type={item.type}
                progress={item.percent}
                currentTime={item.currentTime}
                duration={item.duration}
                onClick={goToContent}
              />
            ))}
          </ScrollRow>
        )}

        {/* Favorites */}
        {favorites.length > 0 && (
          <ScrollRow title="Sevimlilər" icon={Heart}>
            {favorites.map((item) => (
              <PosterCard
                key={item.id}
                id={item.id}
                name={item.name}
                poster={item.poster}
                type={item.type}
                rating={item.rating}
                year={item.year}
                onClick={goToContent}
              />
            ))}
          </ScrollRow>
        )}

        {/* Movies */}
        {movies.length > 0 && (
          <ScrollRow title="Filmlər" icon={Film} viewAllPath="/app/movies">
            {movies.map((m) => (
              <PosterCard
                key={m.stream_id}
                id={m.stream_id}
                name={m.name}
                poster={m.stream_icon}
                type="vod"
                rating={m.rating_5based}
                year={(m.release_date || "").slice(0, 4)}
                onClick={goToContent}
              />
            ))}
          </ScrollRow>
        )}

        {/* Series */}
        {series.length > 0 && (
          <ScrollRow title="Seriallar" icon={Clapperboard} viewAllPath="/app/series">
            {series.map((s) => (
              <PosterCard
                key={s.series_id}
                id={s.series_id}
                name={s.name}
                poster={s.cover}
                type="series"
                rating={s.rating_5based}
                year={(s.release_date || "").slice(0, 4)}
                onClick={goToContent}
              />
            ))}
          </ScrollRow>
        )}

        {/* Watch History */}
        {watchHistory.length > 0 && (
          <ScrollRow title="Son baxılanlar" icon={Clock}>
            {watchHistory.map((item) => (
              <LandscapeCard
                key={item.id}
                id={item.id}
                name={item.name}
                poster={item.poster}
                type={item.type}
                year={item.year}
                compact
                onClick={goToContent}
              />
            ))}
          </ScrollRow>
        )}
      </div>

      {/* Movie Detail Modal */}
      {selectedMovie && (
        <MovieDetailModal
          movie={selectedMovie}
          onClose={() => setSelectedMovie(null)}
          onPlay={handlePlayMovie}
        />
      )}
    </div>
  );
}
